/* global G */
import { actionWithHooks } from 'lib/sequence/module/action'
import { setData } from 'lib/sequence/model/api/set'
import sequenceModelSubmit from 'lib/sequence/model/api/submit'
import seqReset from 'lib/sequence/model/api/reset'
import { showBlockingDialog } from 'app/_shared/events/dialog'
import showToast from 'app/_shared/component/showToast'
import { getMaxSeenDate } from 'app/xEcoApm/helper'
import { UPD_LIC, UPD_LIC_PARAMS } from 'app/xEcoApm/helper/apm'
import { BLE_PERIPHERAL_CONNECTION_STATUS } from 'model/_shared/collection/bluetooth/constants'
import { persistSensor } from 'app/xEcoApm/helper/offline'

type LicenceUpdateCode = {
  serial: string,
  license_counter: number,
  duration_months: number,
  signature: string
}


/**
 * Shows a toast informing the user that a sensor's license could be successfully activated.
 *
 * @param module  current module
 * @param nick    sensor's nick property
 */
const showLicenceUpdatedToast = async (module: Gaia.AppModule.Spec, nick: string) => await showToast(module, {
  message: {
    ns: 'apmGo',
    key: 'toast.licence.updated.message',
    defaultValue: 'Sensor {{nick}} license has been updated',
    nick,
  },
  severity: 'info',
  duration: 3000,
  close: true,
})

/**
 * Shows a dialog informing the user that a sensor's license could not be activated because the
 * target device has been disconnected.
 *
 * @param module  current module
 * @param nick    sensor's nick property
 */
const showDeviceDisconnectedErrorDialog = async (module: Gaia.AppModule.Spec, nick: string) => await showBlockingDialog(module, null, {
  title: {
    ns: 'apmGo',
    key: 'dialog.licenceUpdateDeviceDisconnectedError.title',
    defaultValue: 'Error',
    nick,
  },
  text: {
    ns: 'apmGo',
    key: 'dialog.licenceUpdateDeviceDisconnectedError.text',
    defaultValue: 'Sensor {{nick}} license could not be updated because it is not connected',
    nick,
  },
  cancel: false,
})

/**
 * Shows a dialog informing the user that a sensor's license could not be activated.
 *
 * @param module  current module
 * @param nick    sensor's nick property
 */
const showLicenceErrorDialog = async (module: Gaia.AppModule.Spec, nick: string) => await showBlockingDialog(module, null, {
  title: {
    ns: 'apmGo',
    key: 'dialog.licenceUpdateError.title',
    defaultValue: 'Error',
    nick,
  },
  text: {
    ns: 'apmGo',
    key: 'data.licenceUpdateError.text',
    defaultValue: 'Sensor {{nick}} license could not be updated',
    nick,
  },
  cancel: false,
})

/**
 * Maps incoming `code` properties to the `UPD_LIC_PARAMS` structure.
 *
 * @param code mapping target
 */
const normalize = (code: LicenceUpdateCode): UPD_LIC_PARAMS => ({
  serial: code.serial,
  licenseCounter: code.license_counter,
  durationMonths: code.duration_months,
  signature: code.signature,
})

/**
 * Attempts to update the paired sensor's license with the code passed through `args`.
 *
 * @param obj current module
 */
const licenceUpdate = (obj: Gaia.AppModule.Spec) => async (...args: any[]) => {
  const { code } = args?.[0] || {}

  if (!code) {
    return args
  }

  const online = obj[G.ADAPTER][G.SESSION][G.STATE][G.ONLINE]
  const eventBus = obj[G.ADAPTER][G.EVENTS]
  const bluetoothAdapter = obj[G.ADAPTER][G.BLUETOOTH]
  const { sensor, pairing } = obj[G.MODEL][G.CHILDREN]

  const { nick, name } = sensor[G.CACHE].value
  const peripheral = bluetoothAdapter[G.GROUP][name]

  const { connectionStatus } = peripheral?.[G.STATE] || {}
  const connected = connectionStatus === BLE_PERIPHERAL_CONNECTION_STATUS.CONNECTED

  if (!connected) {
    await showDeviceDisconnectedErrorDialog(obj, nick)
    return args
  }

  eventBus.dispatch(eventBus.type(G.LOAD, G.INIT))
  const request = normalize(code)

  try {
    request.date = getMaxSeenDate(obj)
    const peripheral = bluetoothAdapter[G.GROUP][name]

    const response = await peripheral[G.API].send(UPD_LIC(request))

    if (response.s !== 0) {
      await showLicenceErrorDialog(obj, nick)
      return args
    }

    setData(pairing, {
      ...sensor[G.CACHE].value,
      licensedUntil: response.e,
    }, { updateCache: true })

    if (online) {
      await sequenceModelSubmit(pairing)
    } else {
      await persistSensor(obj)
    }

    seqReset(sensor)

    await showLicenceUpdatedToast(obj, nick)
    await actionWithHooks(obj[G.STATE][G.ACTION])([])
  } catch (error) {
    console.error(error)
    await showLicenceErrorDialog(obj, nick)
  } finally {
    eventBus.dispatch(eventBus.type(G.LOAD, G.DONE))
  }

  return args
}

export default licenceUpdate
